/**
 * The start of the Pro trial in the CRM iframe (#2816): the server starts it
 * on the next analysis, at a moment the screen cannot know. While the trial
 * waits for it, the session is re-read when the user comes back to the
 * iframe — window focus or the tab shown again — at most once per
 * `TRIAL_START_REFRESH_MS`.
 *
 * Not on a timer: a re-read of the embedded session renews it, and a timer
 * would keep an idle tab's session alive for good — only the user's own
 * activity may. The web app asks from a visible tab instead (`refetchInterval`
 * of the flag query).
 */
import { useEffect } from 'react';
import type { ProTrialView } from '@aica/api-types/schemas';
import { TRIAL_START_REFRESH_MS } from '@/lib/pro-trial';

export function useTrialStartRefreshOnFocus(
  proTrial: ProTrialView | null | undefined,
  refresh: () => unknown
): void {
  const waiting = proTrial?.state === 'available';
  useEffect(() => {
    if (!waiting) return;
    // The answer that said `available` was just read.
    let lastRead = Date.now();
    const onActive = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastRead < TRIAL_START_REFRESH_MS) return;
      lastRead = now;
      void refresh();
    };
    window.addEventListener('focus', onActive);
    document.addEventListener('visibilitychange', onActive);
    return () => {
      window.removeEventListener('focus', onActive);
      document.removeEventListener('visibilitychange', onActive);
    };
  }, [waiting, refresh]);
}
